/**
 * Storybook preview carousel — product allowlist only.
 *
 * Enhances Customily `.customily-modal-preview-only` into a 2-slide viewer:
 * Cover (personalized) ↔ Page 1 (personalized).
 * Does not own ATC/close; keeps `.cl-preview-image` in DOM for existing ATC overlay.
 *
 * Capture strategy: Case B base + v2 in-modal background dual-capture.
 * Modal image stays stable on Preview Position switch; gallery zoom src changes.
 * Missing position is captured once in the background while the modal is open.
 */
(function () {
  // MAINTENANCE: PRODUCT_HANDLES must stay in sync with the Liquid guard in
  // sections/main-product.liquid (search: storybook-preview-carousel.js).
  // When adding a second handle, update BOTH this object AND the Liquid if-block.
  var PRODUCT_HANDLES = {
    'good-night-my-love-sleeping-teddy-bear-moon-and-stars-personalized-kids-story-book-kid803_pkibo': true,
  };

  var POSITION_NAME = 'properties[Preview Position]';
  var POSITIONS = ['Cover', 'Page 1'];
  // MAINTENANCE: PERSONALIZATION_NAMES must match the Customily field names
  // configured for this product in the Customily dashboard. If a field is
  // renamed there, update this list to keep cache-key signatures consistent.
  var PERSONALIZATION_NAMES = [
    'properties[My love, ...]',
    'properties[To My ...]',
    'properties[Type Signature]',
  ];
  var MODAL_SELECTOR = '.customily-modal-preview-only';
  var MAIN_SELECTOR = '.main.cl-use-image';
  var WRAPPER_SELECTOR = '.cl-preview-wrapper';
  var PREVIEW_IMG_SELECTOR = '.cl-preview-image';
  var ROOT_CLASS = 'cl-storybook-carousel';
  var STYLE_ID = 'cl-storybook-carousel-style';
  var BODY_MODAL_OPEN_CLASS = 'cl-storybook-carousel-modal-open';
  var PREVIEW_IMG_HIDE_STYLE =
    'position:absolute!important;width:1px!important;height:1px!important;opacity:0!important;' +
    'pointer-events:none!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;';

  var cache = Object.create(null);
  var cacheSignature = '';
  var toDataUrlUsed = Object.create(null);
  var enhancedModals = new WeakSet();
  // pendingTimers tracks IDs for non-capture setTimeout calls so swallowed
  // exceptions don't leave orphaned callbacks. IDs are deleted when the
  // callback fires. Cancellation outside schedule() (e.g. on page unload) will
  // leave stale IDs in the Set — acceptable because a full page navigation
  // resets the IIFE scope entirely.
  var pendingTimers = new Set();

  var capture = {
    state: 'idle', // idle | running | done | failed
    signature: '',
    originalPos: '',
    missingPos: '',
    switched: false,
    aborted: false,
    modal: null,
    ui: null,
    timers: new Set(),
  };

  function isTargetProduct() {
    var match = String(location.pathname || '').match(/\/products\/([^/?#]+)/);
    return !!(match && PRODUCT_HANDLES[match[1]]);
  }

  function injectStyles() {
    var style = document.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }
    // Image constraints mirror theme .cl-preview-image in section-main-product.css.
    // Rules are joined into a single string at runtime; each entry is one CSS rule.
    var C = ROOT_CLASS;
    var B = BODY_MODAL_OPEN_CLASS;
    style.textContent = [
      '.' + C + '{display:block;width:100%;margin:0;padding:0;box-sizing:border-box}',
      '.' + C + '__stage{position:relative;display:flex;align-items:center;justify-content:center;width:100%;line-height:0}',
      '.' + C + '__img{display:block;width:auto;height:auto;max-width:min(100%,48rem);max-height:min(60vh,calc(100dvh - 22rem));margin:0 auto;object-fit:contain;border-radius:.8rem;background:transparent;user-select:none;-webkit-user-drag:none}',
      '.' + C + '__placeholder{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.85rem;width:100%;max-width:min(100%,48rem);aspect-ratio:1/1;max-height:min(60vh,calc(100dvh - 22rem));margin:0 auto;padding:1.6rem;box-sizing:border-box;border-radius:.8rem;background:rgba(63,52,36,.06);color:#3f3424;text-align:center;line-height:1.35}',
      '.' + C + '__placeholder[hidden]{display:none!important}',
      '.' + C + '__placeholder[data-loading="true"]{opacity:.92}',
      '.' + C + '__placeholder-text{max-width:16rem;color:rgba(63,52,36,.65);font-size:1.2rem;font-weight:500;line-height:1.35}',
      '.' + C + '__spinner{width:3.2rem;height:3.2rem;border-radius:50%;border:3px solid rgba(63,52,36,.15);border-top-color:#3f3424;animation:cl-storybook-spin .8s linear infinite;flex:0 0 auto;box-sizing:border-box}',
      '.' + C + '__spinner[hidden]{display:none!important}',
      '.' + C + '__decode{position:absolute;inset:0;z-index:1;display:flex;align-items:center;justify-content:center;pointer-events:none;background:rgba(255,255,255,.28);border-radius:.8rem}',
      '.' + C + '__decode[hidden]{display:none!important}',
      '@keyframes cl-storybook-spin{to{transform:rotate(360deg)}}',
      '@media (prefers-reduced-motion:reduce){.' + C + '__spinner{animation:none;border-top-color:rgba(63,52,36,.45);opacity:.9}}',
      '.' + C + '__nav{position:absolute;top:50%;transform:translateY(-50%);z-index:2;width:3.6rem;height:3.6rem;border:none;border-radius:999px;background:rgba(63,52,36,.82);color:#fff;font-size:1.8rem;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0}',
      '.' + C + '__nav:disabled{opacity:.35;cursor:default}',
      '.' + C + '__nav--prev{left:.2rem}',
      '.' + C + '__nav--next{right:.2rem}',
      '.' + C + '__label,.' + C + '__counter{position:absolute;bottom:.55rem;z-index:3;pointer-events:none;max-width:46%;padding:.22rem .65rem;border-radius:999px;background:rgba(63,52,36,.78);color:#fff;font-size:1.2rem;font-weight:600;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;box-shadow:0 1px 2px rgba(0,0,0,.18)}',
      '.' + C + '__label{left:.55rem}',
      '.' + C + '__counter{right:.55rem;font-weight:500}',
      '.' + C + '__status{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}',
      // Customily Tippy swatch tooltips stick after programmatic label clicks.
      'body.' + B + ' .tippy-popper{display:none!important;visibility:hidden!important;opacity:0!important;pointer-events:none!important}',
      '@media (min-width:750px){' +
        '.' + C + '__img{max-width:min(100%,52rem);max-height:min(66vh,calc(100dvh - 22rem))}' +
        '.' + C + '__placeholder{max-width:min(100%,52rem);max-height:min(66vh,calc(100dvh - 22rem))}' +
      '}',
      '@media (max-width:749px){' +
        '.' + C + '__img{max-width:100%;max-height:min(50vh,calc(100dvh - 19rem))}' +
        '.' + C + '__placeholder{max-width:100%;max-height:min(50vh,calc(100dvh - 19rem))}' +
        '.' + C + '__nav{width:3.2rem;height:3.2rem;font-size:1.6rem}' +
      '}',
    ].join('');
  }

  function usableSrc(src, img) {
    if (!src || src === 'about:blank' || src.indexOf('data:,') === 0) return false;
    if (String(src).toLowerCase().indexOf('data:image/svg') === 0) return false;
    if (img && img.complete && (img.naturalWidth <= 0 || img.naturalHeight <= 0)) return false;
    if (String(src).indexOf('data:image') === 0 && src.length < 128) return false;
    return true;
  }

  function normalizeSrcKey(src) {
    if (!src) return '';
    var value = String(src);
    if (value.indexOf('data:') === 0) {
      return 'data:' + value.length + ':' + value.slice(100, 132) + ':' + value.slice(-32);
    }
    try {
      var url = new URL(value, location.origin);
      return (url.origin + url.pathname).toLowerCase();
    } catch (err) {
      return value.split('?')[0].split('#')[0].toLowerCase();
    }
  }

  function getSelectedPosition() {
    var el = document.querySelector('input[name="' + POSITION_NAME + '"]:checked');
    return el ? String(el.value || '').trim() : '';
  }

  function getPositionInput(value) {
    var inputs = document.querySelectorAll('input[name="' + POSITION_NAME + '"]');
    for (var i = 0; i < inputs.length; i++) {
      if (String(inputs[i].value || '').trim() === value) return inputs[i];
    }
    return null;
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/([^a-zA-Z0-9_-])/g, '\\$1');
  }

  function dismissPointerChrome(target) {
    if (!target) return;
    var types = ['pointerout', 'pointerleave', 'mouseout', 'mouseleave'];
    for (var i = 0; i < types.length; i++) {
      var type = types[i];
      try {
        if (type.indexOf('pointer') === 0 && typeof PointerEvent === 'function') {
          target.dispatchEvent(new PointerEvent(type, { bubbles: true, cancelable: true }));
        } else {
          target.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
        }
      } catch (err) {
        target.dispatchEvent(new Event(type, { bubbles: true, cancelable: true }));
      }
    }
    if (typeof target.blur === 'function') {
      try {
        target.blur();
      } catch (err2) {
        /* ignore */
      }
    }
  }

  function clickPosition(value) {
    var input = getPositionInput(value);
    if (!input || input.checked) return false;

    var label = null;
    if (input.id) label = document.querySelector('label[for="' + cssEscape(input.id) + '"]');
    if (!label) label = input.closest('label');

    if (label) label.click();
    else {
      input.checked = true;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.click();
    }
    // Programmatic swatch clicks leave Tippy tooltips stuck in hover state.
    dismissPointerChrome(label);
    dismissPointerChrome(input);
    return true;
  }

  function syncBodyModalOpenClass() {
    var open = false;
    var modals = document.querySelectorAll(MODAL_SELECTOR);
    for (var i = 0; i < modals.length; i++) {
      if (isModalVisible(modals[i])) {
        open = true;
        break;
      }
    }
    document.body.classList.toggle(BODY_MODAL_OPEN_CLASS, open);
  }

  function getCacheKeySignature() {
    var parts = [];
    for (var i = 0; i < PERSONALIZATION_NAMES.length; i++) {
      var input = document.querySelector('input[name="' + PERSONALIZATION_NAMES[i] + '"]');
      parts.push(input ? String(input.value || '').trim() : '');
    }
    return parts.join('\u0001');
  }

  function getCaptureTaskSignature() {
    return getCacheKeySignature() + '\u0001' + getSelectedPosition();
  }

  function invalidateCacheIfSignatureChanged() {
    var signature = getCacheKeySignature();
    if (signature === cacheSignature) return false;
    cache = Object.create(null);
    cacheSignature = signature;
    toDataUrlUsed = Object.create(null);
    if (capture.state !== 'running') {
      capture.state = 'idle';
      capture.signature = '';
    }
    return true;
  }

  function isModalVisible(modal) {
    if (!modal || !document.contains(modal)) return false;
    var style = window.getComputedStyle(modal);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    // getBoundingClientRect triggers layout. Acceptable here because this
    // product only ever has 1-2 modals; revisit if modal count grows.
    var rect = modal.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function readModalPreviewSrc(modal) {
    var img = modal && modal.querySelector(PREVIEW_IMG_SELECTOR);
    if (!img) return '';
    var src = img.currentSrc || img.src || '';
    return usableSrc(src, img) ? src : '';
  }

  function isCustomilyGeneratedSrc(src) {
    if (!usableSrc(src)) return false;
    if (String(src).indexOf('data:image') === 0) return false;
    return /customily\.com|customily-generated|shopify\/assetFiles/i.test(src);
  }

  function getGallery() {
    return document.querySelector('body.template-product .customily_gallery');
  }

  function readGeneratedGallerySrc() {
    var gallery = getGallery();
    if (!gallery) return '';

    // Fast path: Customily helper caches the latest generated src on the element.
    if (gallery.__clCachedPreviewSrc && usableSrc(gallery.__clCachedPreviewSrc)) {
      var cached = gallery.__clCachedPreviewSrc;
      if (isCustomilyGeneratedSrc(cached) || String(cached).indexOf('data:image') === 0) {
        return cached;
      }
    }

    // Slow path: walk preferred scopes zoom-first → whole gallery.
    // Zoom scope is the active swiper slide or canvas container; it is more
    // likely to hold the freshly rendered image than the wider gallery root.
    var zoom =
      gallery.querySelector('.customily_gallery_media .swiper-slide-active') ||
      gallery.querySelector('.customily_gallery_media .canvas-container') ||
      gallery.querySelector('.customily_gallery_media [data-slide]') ||
      gallery.querySelector('.canvas-thumbnail') ||
      null;

    var scopes = [];
    if (zoom) scopes.push(zoom);
    scopes.push(gallery);

    for (var s = 0; s < scopes.length; s++) {
      var imgs = scopes[s].querySelectorAll('img');
      for (var i = 0; i < imgs.length; i++) {
        var src = imgs[i].currentSrc || imgs[i].src || '';
        if (isCustomilyGeneratedSrc(src)) return src;
        if (String(src).indexOf('data:image') === 0 && usableSrc(src)) return src;
      }
    }

    return '';
  }

  function readCaptureSrcOnce(position) {
    var gallery = getGallery();
    if (
      gallery &&
      window.clGalleryThumbnailsUx &&
      typeof window.clGalleryThumbnailsUx.getCurrentPreviewImage === 'function'
    ) {
      try {
        var helperSrc = window.clGalleryThumbnailsUx.getCurrentPreviewImage(gallery);
        if (usableSrc(helperSrc)) return helperSrc;
      } catch (err) {
        /* fall through to one-shot canvas export */
      }
    }

    var generated = readGeneratedGallerySrc();
    if (usableSrc(generated)) return generated;

    var signature = getCacheKeySignature();
    var key = signature + '\u0001' + position;
    if (toDataUrlUsed[key]) return '';
    toDataUrlUsed[key] = true;

    var canvas =
      (gallery && gallery.querySelector('.lower-canvas, canvas')) ||
      document.querySelector('body.template-product .customily_gallery canvas');
    if (!canvas || !canvas.width || !canvas.height) return '';
    try {
      var dataUrl = canvas.toDataURL('image/png');
      return usableSrc(dataUrl) ? dataUrl : '';
    } catch (err2) {
      return '';
    }
  }

  function storeCache(position, src) {
    if (POSITIONS.indexOf(position) === -1) return;
    if (!usableSrc(src)) return;
    invalidateCacheIfSignatureChanged();
    // B3: reject src that already belongs to another position (gallery re-render lag).
    var srcKey = normalizeSrcKey(src);
    for (var i = 0; i < POSITIONS.length; i++) {
      if (POSITIONS[i] !== position && cache[POSITIONS[i]] && normalizeSrcKey(cache[POSITIONS[i]]) === srcKey) {
        return;
      }
    }
    cache[position] = src;
  }

  function rememberFromModal(modal) {
    var pos = getSelectedPosition();
    var src = readModalPreviewSrc(modal);
    if (usableSrc(src)) storeCache(pos, src);
  }

  function rememberFromGalleryPassive(position) {
    if (capture.state === 'running') return;
    var pos = position || getSelectedPosition();
    if (POSITIONS.indexOf(pos) === -1) return;

    var generated = readGeneratedGallerySrc();
    if (usableSrc(generated)) {
      storeCache(pos, generated);
      return;
    }

    var once = readCaptureSrcOnce(pos);
    if (usableSrc(once)) storeCache(pos, once);
  }



  function clearCaptureTimers() {
    capture.timers.forEach(function (id) { clearTimeout(id); });
    capture.timers = new Set();
  }

  function schedule(fn, ms) {
    var id = setTimeout(function () {
      pendingTimers.delete(id);
      try { fn(); } catch (err) { /* swallow — scheduled callbacks must not throw */ }
    }, ms);
    pendingTimers.add(id);
    return id;
  }

  function scheduleCapture(fn, ms) {
    var id = setTimeout(function () {
      capture.timers.delete(id);
      try { fn(); } catch (err) { /* swallow — capture callbacks must not throw */ }
    }, ms);
    capture.timers.add(id);
    return id;
  }

  function loadingHint(position) {
    return 'Preparing ' + position + '…';
  }

  function placeholderHint(position) {
    return 'Switch Preview Position to ' + position + ' and press Preview again';
  }

  function findMissingPositions() {
    var missing = [];
    for (var i = 0; i < POSITIONS.length; i++) {
      if (!usableSrc(cache[POSITIONS[i]])) missing.push(POSITIONS[i]);
    }
    return missing;
  }

  function buildSlides(modal, options) {
    invalidateCacheIfSignatureChanged();
    options = options || {};
    var loadingPositions = options.loadingPositions || [];
    var slides = [];

    POSITIONS.forEach(function (pos) {
      if (usableSrc(cache[pos])) {
        slides.push({ src: cache[pos], label: pos, kind: 'personalized' });
      } else if (loadingPositions.indexOf(pos) !== -1) {
        slides.push({
          src: '',
          label: pos,
          kind: 'loading',
          hint: loadingHint(pos),
        });
      } else {
        slides.push({
          src: '',
          label: pos,
          kind: 'placeholder',
          hint: placeholderHint(pos),
        });
      }
    });

    var hasVisual = slides.some(function (slide) {
      return slide.kind === 'personalized' && usableSrc(slide.src);
    });
    if (!hasVisual) {
      var modalSrc = readModalPreviewSrc(modal || document.querySelector(MODAL_SELECTOR));
      if (usableSrc(modalSrc)) {
        var selected = getSelectedPosition() || POSITIONS[0];
        slides = POSITIONS.map(function (pos) {
          if (pos === selected) {
            return { src: modalSrc, label: pos, kind: 'personalized' };
          }
          if (loadingPositions.indexOf(pos) !== -1) {
            return { src: '', label: pos, kind: 'loading', hint: loadingHint(pos) };
          }
          return { src: '', label: pos, kind: 'placeholder', hint: placeholderHint(pos) };
        });
      }
    }

    return slides;
  }

  function pickInitialIndex(slides) {
    if (!slides || !slides.length) return 0;
    var selected = getSelectedPosition();
    var i;
    if (selected) {
      // Prefer the selected Preview Position slide even when it is still
      // loading/placeholder (no usable src yet). Requiring usableSrc here
      // forced Cover-first opens whenever Cover was already cached.
      for (i = 0; i < slides.length; i++) {
        if (slides[i].label === selected) {
          return i;
        }
      }
    }
    for (i = 0; i < slides.length; i++) {
      if (slides[i].kind === 'personalized' && usableSrc(slides[i].src)) {
        return i;
      }
    }
    return 0;
  }

  function mountCarousel(modal, slides) {
    var main = modal.querySelector(MAIN_SELECTOR);
    var wrapper = main && main.querySelector(WRAPPER_SELECTOR);
    if (!main || !wrapper || !slides.length) return null;

    var existing = wrapper.querySelector('.' + ROOT_CLASS);
    if (existing) existing.remove();

    var root = document.createElement('div');
    root.className = ROOT_CLASS;
    root.setAttribute('data-loading', 'false');

    var stage = document.createElement('div');
    stage.className = ROOT_CLASS + '__stage';

    var prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = ROOT_CLASS + '__nav ' + ROOT_CLASS + '__nav--prev';
    prevBtn.setAttribute('aria-label', 'Previous page');
    prevBtn.textContent = '‹';

    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = ROOT_CLASS + '__nav ' + ROOT_CLASS + '__nav--next';
    nextBtn.setAttribute('aria-label', 'Next page');
    nextBtn.textContent = '›';

    var image = document.createElement('img');
    image.className = ROOT_CLASS + '__img';
    image.alt = 'Storybook preview';

    var placeholder = document.createElement('div');
    placeholder.className = ROOT_CLASS + '__placeholder';
    placeholder.hidden = true;
    placeholder.style.display = 'none';

    var spinner = document.createElement('div');
    spinner.className = ROOT_CLASS + '__spinner';
    spinner.hidden = true;
    spinner.setAttribute('aria-hidden', 'true');

    var placeholderText = document.createElement('div');
    placeholderText.className = ROOT_CLASS + '__placeholder-text';
    placeholder.appendChild(spinner);
    placeholder.appendChild(placeholderText);

    var decodeOverlay = document.createElement('div');
    decodeOverlay.className = ROOT_CLASS + '__decode';
    decodeOverlay.hidden = true;
    decodeOverlay.style.display = 'none';
    decodeOverlay.setAttribute('aria-hidden', 'true');
    var decodeSpinner = document.createElement('div');
    decodeSpinner.className = ROOT_CLASS + '__spinner';
    decodeSpinner.setAttribute('aria-hidden', 'true');
    decodeOverlay.appendChild(decodeSpinner);

    var labelEl = document.createElement('div');
    labelEl.className = ROOT_CLASS + '__label';
    var counterEl = document.createElement('div');
    counterEl.className = ROOT_CLASS + '__counter';

    var statusEl = document.createElement('div');
    statusEl.className = ROOT_CLASS + '__status';
    statusEl.setAttribute('aria-live', 'polite');

    stage.appendChild(prevBtn);
    stage.appendChild(image);
    stage.appendChild(placeholder);
    stage.appendChild(decodeOverlay);
    stage.appendChild(labelEl);
    stage.appendChild(counterEl);
    stage.appendChild(nextBtn);
    root.appendChild(stage);
    root.appendChild(statusEl);

    var previewImg = wrapper.querySelector(PREVIEW_IMG_SELECTOR);
    if (previewImg) {
      previewImg.setAttribute('style', PREVIEW_IMG_HIDE_STYLE);
      wrapper.insertBefore(root, previewImg);
    } else {
      wrapper.insertBefore(root, wrapper.firstChild);
    }

    var index = pickInitialIndex(slides);
    var touchX = 0;
    var touchY = 0;
    var touching = false;
    var decodeCleanup = null;

    function clearDecodeWait() {
      if (typeof decodeCleanup === 'function') {
        decodeCleanup();
        decodeCleanup = null;
      }
      decodeOverlay.hidden = true;
      decodeOverlay.style.display = 'none';
    }

    function waitForImageDecode() {
      clearDecodeWait();
      if (image.complete && image.naturalWidth > 0) return;
      decodeOverlay.hidden = false;
      decodeOverlay.style.display = '';
      var done = function () {
        clearDecodeWait();
      };
      image.addEventListener('load', done);
      image.addEventListener('error', done);
      decodeCleanup = function () {
        image.removeEventListener('load', done);
        image.removeEventListener('error', done);
      };
    }

    function render() {
      var slide = slides[index];
      if (!slide) return;

      var isEmpty = slide.kind === 'placeholder' || slide.kind === 'loading' || !usableSrc(slide.src);
      if (isEmpty) {
        clearDecodeWait();
        image.hidden = true;
        image.style.display = 'none';
        image.removeAttribute('src');
        image.alt = '';
        placeholder.hidden = false;
        placeholder.style.display = '';
        placeholder.setAttribute('data-loading', slide.kind === 'loading' ? 'true' : 'false');
        spinner.hidden = slide.kind !== 'loading';
        placeholderText.textContent =
          slide.hint ||
          (slide.kind === 'loading' ? loadingHint(slide.label || 'preview') : placeholderHint(slide.label || 'this page'));
      } else {
        placeholder.hidden = true;
        placeholder.style.display = 'none';
        placeholder.setAttribute('data-loading', 'false');
        spinner.hidden = true;
        placeholderText.textContent = '';
        image.hidden = false;
        image.style.display = '';
        if (image.src !== slide.src) {
          image.src = slide.src;
          waitForImageDecode();
        } else if (!image.complete || image.naturalWidth <= 0) {
          waitForImageDecode();
        } else {
          clearDecodeWait();
        }
        image.alt = slide.label + ' preview';
      }

      labelEl.textContent = slide.label;
      counterEl.textContent = index + 1 + ' / ' + slides.length;
      prevBtn.disabled = index <= 0;
      nextBtn.disabled = index >= slides.length - 1;
    }

    function go(delta) {
      var next = index + delta;
      if (next < 0 || next >= slides.length) return;
      index = next;
      render();
    }

    prevBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      go(-1);
    });
    nextBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      go(1);
    });

    stage.addEventListener(
      'touchstart',
      function (e) {
        if (!e.changedTouches || !e.changedTouches[0]) return;
        touching = true;
        touchX = e.changedTouches[0].clientX;
        touchY = e.changedTouches[0].clientY;
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchend',
      function (e) {
        if (!touching || !e.changedTouches || !e.changedTouches[0]) return;
        touching = false;
        var dx = e.changedTouches[0].clientX - touchX;
        var dy = e.changedTouches[0].clientY - touchY;
        if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
        go(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );

    render();
    return {
      root: root,
      setLoading: function (on, message) {
        root.setAttribute('data-loading', on ? 'true' : 'false');
        statusEl.textContent = message || '';
      },
      updateSlides: function (nextSlides) {
        if (!nextSlides || !nextSlides.length) return;
        slides = nextSlides;
        if (index >= slides.length) index = slides.length - 1;
        render();
      },
    };
  }

  function restoreOriginalPositionSync() {
    if (!capture.switched || !capture.originalPos) return false;
    var restored = clickPosition(capture.originalPos);
    capture.switched = false;
    return restored;
  }

  function abortCapture(reason) {
    if (capture.state !== 'running') return false;
    clearCaptureTimers();
    capture.aborted = true;
    restoreOriginalPositionSync();

    var modal = capture.modal;
    var ui = capture.ui;
    var missingPos = capture.missingPos;

    // Close / regenerate: allow retry on next enhance. ATC: failed until signature changes.
    if (reason === 'close' || reason === 'preview') {
      capture.state = 'idle';
      capture.signature = '';
    } else {
      capture.state = 'failed';
    }

    capture.modal = null;
    capture.ui = null;

    if (ui && modal && isModalVisible(modal) && missingPos && !usableSrc(cache[missingPos])) {
      ui.updateSlides(buildSlides(modal, { loadingPositions: [] }));
      ui.setLoading(false, '');
    }
    return true;
  }

  function finishCaptureSuccess(src) {
    if (capture.state !== 'running' || capture.aborted) return;
    storeCache(capture.missingPos, src);
    restoreOriginalPositionSync();

    var modal = capture.modal;
    var ui = capture.ui;
    clearCaptureTimers();

    // Best-effort wait for gallery restore, then mark done.
    scheduleCapture(function () {
      if (capture.aborted) return;
      capture.state = 'done';
      capture.modal = null;
      capture.ui = null;
      if (ui && modal && document.contains(modal)) {
        ui.updateSlides(buildSlides(modal));
        ui.setLoading(false, '');
      }
    }, 300);

    // Soft restore wait cap (no polling of canvas).
    scheduleCapture(function () {
      /* noop — keeps restore window bounded under 3s via timer list clear on abort */
    }, 3000);
  }

  function finishCaptureFailure() {
    if (capture.state !== 'running' || capture.aborted) return;
    restoreOriginalPositionSync();
    clearCaptureTimers();
    capture.state = 'failed';

    var modal = capture.modal;
    var ui = capture.ui;
    capture.modal = null;
    capture.ui = null;

    if (ui && modal && document.contains(modal)) {
      ui.updateSlides(buildSlides(modal, { loadingPositions: [] }));
      ui.setLoading(false, '');
    }
  }

  function waitForGalleryKeyChange(baselineKey, timeoutMs, onDone) {
    var started = Date.now();

    function tick() {
      if (capture.state !== 'running' || capture.aborted) return;
      if (!isModalVisible(capture.modal)) {
        abortCapture('close');
        return;
      }

      var currentKey = normalizeSrcKey(readGeneratedGallerySrc());
      var changed = currentKey && currentKey !== baselineKey;
      var timedOut = Date.now() - started >= timeoutMs;

      if (changed || timedOut) {
        onDone(changed);
        return;
      }

      scheduleCapture(tick, 300);
    }

    scheduleCapture(tick, 300);
  }

  function startBackgroundCapture(modal, ui, missingPos) {
    if (!modal || !ui || !missingPos) return;
    if (capture.state === 'running') return;
    if (usableSrc(cache[missingPos])) return;

    var originalPos = getSelectedPosition();
    if (!originalPos || originalPos === missingPos) return;
    if (!getPositionInput(missingPos) || !getPositionInput(originalPos)) return;

    var taskSignature = getCaptureTaskSignature();
    if (capture.state === 'done' && capture.signature === taskSignature && usableSrc(cache[missingPos])) {
      return;
    }
    if (capture.state === 'failed' && capture.signature === taskSignature) {
      return;
    }

    capture.state = 'running';
    capture.signature = taskSignature;
    capture.originalPos = originalPos;
    capture.missingPos = missingPos;
    capture.switched = false;
    capture.aborted = false;
    capture.modal = modal;
    capture.ui = ui;
    clearCaptureTimers();

    // Loading copy lives on the slide only — avoid duplicate status-line text.
    ui.setLoading(true, '');

    var baselineKey = normalizeSrcKey(readGeneratedGallerySrc());
    var switched = clickPosition(missingPos);
    if (!switched) {
      finishCaptureFailure();
      return;
    }
    capture.switched = true;

    waitForGalleryKeyChange(baselineKey, 5000, function (changed) {
      if (capture.state !== 'running' || capture.aborted) return;
      if (!isModalVisible(capture.modal)) {
        abortCapture('close');
        return;
      }

      // B1: if gallery key never changed (timeout), don't read stale src.
      if (!changed) {
        finishCaptureFailure();
        return;
      }

      var src = readCaptureSrcOnce(capture.missingPos);
      if (usableSrc(src)) finishCaptureSuccess(src);
      else finishCaptureFailure();
    });
  }

  function enhanceModal(modal) {
    if (!modal || !isTargetProduct() || !isModalVisible(modal)) return;

    var main = modal.querySelector(MAIN_SELECTOR);
    var wrapper = main && main.querySelector(WRAPPER_SELECTOR);
    var previewImg = wrapper && wrapper.querySelector(PREVIEW_IMG_SELECTOR);
    if (!main || !wrapper || !previewImg) return;

    injectStyles();
    syncBodyModalOpenClass();
    invalidateCacheIfSignatureChanged();
    rememberFromModal(modal);

    var missing = findMissingPositions();
    var canCapture =
      missing.length > 0 &&
      capture.state !== 'running' &&
      !(capture.state === 'failed' && capture.signature === getCaptureTaskSignature());

    var loadingPositions = canCapture ? missing.slice(0, 1) : [];
    var slides = buildSlides(modal, { loadingPositions: loadingPositions });
    var ui = mountCarousel(modal, slides);
    if (!ui) return;

    enhancedModals.add(modal);
    ui.setLoading(false, '');

    // Modal preview src can arrive after the first enhance tick; re-read once
    // so the selected slide can leave the loading state without remounting.
    schedule(function () {
      if (!isModalVisible(modal) || !ui.root.isConnected) return;
      // B2: skip rememberFromModal if capture is mid-switch — modal img still
      // shows the original position but radio already points at missingPos.
      if (!(capture.state === 'running' && capture.switched)) {
        rememberFromModal(modal);
      }
      var stillLoading =
        capture.state === 'running' && capture.missingPos ? [capture.missingPos] : [];
      ui.updateSlides(buildSlides(modal, { loadingPositions: stillLoading }));
    }, 800);

    if (loadingPositions.length) {
      startBackgroundCapture(modal, ui, loadingPositions[0]);
    }
  }

  function syncEnhancedModalFlags() {
    var modals = document.querySelectorAll(MODAL_SELECTOR);
    for (var i = 0; i < modals.length; i++) {
      var modal = modals[i];
      if (!isModalVisible(modal)) {
        enhancedModals.delete(modal);
        if (capture.state === 'running' && capture.modal === modal) {
          abortCapture('close');
        }
      }
    }
    syncBodyModalOpenClass();
  }

  function tryEnhanceVisibleModals() {
    if (!isTargetProduct()) return;
    syncEnhancedModalFlags();

    var modals = document.querySelectorAll(MODAL_SELECTOR);
    for (var i = 0; i < modals.length; i++) {
      var modal = modals[i];
      if (!isModalVisible(modal)) continue;
      if (enhancedModals.has(modal) && modal.querySelector('.' + ROOT_CLASS)) continue;
      enhanceModal(modal);
    }
    syncBodyModalOpenClass();
  }

  function scheduleEnhanceFromUserAction() {
    if (!isTargetProduct()) return;
    schedule(syncBodyModalOpenClass, 0);
    schedule(syncBodyModalOpenClass, 120);
    // Early enhance shrinks the original→carousel swap window (F3).
    schedule(tryEnhanceVisibleModals, 50);
    schedule(tryEnhanceVisibleModals, 150);
    schedule(tryEnhanceVisibleModals, 300);
    schedule(tryEnhanceVisibleModals, 1000);
  }

  function onPreviewPositionChange() {
    if (!isTargetProduct()) return;
    if (capture.state === 'running') return;
    var pos = getSelectedPosition();
    schedule(function () {
      if (!isTargetProduct() || capture.state === 'running') return;
      rememberFromGalleryPassive(pos);
    }, 600);
  }

  function onPersonalizationChange() {
    if (!isTargetProduct()) return;
    if (capture.state === 'running') abortCapture('preview');
    invalidateCacheIfSignatureChanged();
  }

  function isPersonalizationInput(el) {
    if (!el || !el.name) return false;
    if (el.name === POSITION_NAME) return false;
    return PERSONALIZATION_NAMES.indexOf(el.name) !== -1;
  }

  function isAtcClickTarget(target) {
    if (!target || !target.closest) return false;
    if (target.closest('#customily-cart-btn, #customily-cart-btn-cloned, .cl-atc-overlay')) return true;
    var btn = target.closest('button, [type="submit"], a, [role="button"]');
    if (!btn) return false;
    if (btn.id === 'customily-cart-btn' || btn.id === 'customily-cart-btn-cloned') return true;
    var text = String(btn.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!/^ADD TO CART$/i.test(text)) return false;
    return !!btn.closest(MODAL_SELECTOR) || !!document.getElementById('customily-cart-btn');
  }

  function isPreviewRegenerateClick(target) {
    if (!target || !target.closest) return false;
    var btn = target.closest('button, a, [role="button"]');
    if (!btn) return false;
    if (btn.id === 'customily-preview-button' || btn.id === 'sticky-customily-preview-button') return true;
    var text = String(btn.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    return /PREVIEW YOUR PERSONALIZATION/i.test(text);
  }

  function isModalCloseClick(target) {
    if (!target || !target.closest) return false;
    var modal = target.closest(MODAL_SELECTOR);
    if (!modal) return false;
    // Stable selector check first.
    if (target.closest('[aria-label*="Close" i], .close, .cl-close, .customily-modal-close')) return true;
    // Heuristic fallback: Customily close buttons sometimes use plain ×/X text
    // with no stable class. We detect them by character + small bounding box.
    // This is intentionally loose — false positives inside the modal are safe
    // (aborting capture on an interior click is harmless).
    var text = String(target.textContent || '').trim();
    if (text === 'X' || text === '\u00d7' || text === '\u2715' || text === '\u2716') {
      var rect = target.getBoundingClientRect();
      if (rect.width > 0 && rect.width < 80 && rect.height < 80) return true;
    }
    return false;
  }

  function onCaptureGuardClick(e) {
    if (!isTargetProduct() || capture.state !== 'running') return;
    var t = e.target;
    if (!t) return;

    if (isAtcClickTarget(t)) {
      // SYNCHRONOUS restore before Customily serializes Preview Position.
      abortCapture('atc');
      return;
    }
    if (isPreviewRegenerateClick(t)) {
      abortCapture('preview');
      return;
    }
    if (isModalCloseClick(t)) {
      abortCapture('close');
    }
  }

  function init() {
    if (!isTargetProduct()) return;
    injectStyles();

    // Capture-phase: ATC / close / preview guards must run before Customily handlers.
    document.addEventListener('click', onCaptureGuardClick, true);

    document.addEventListener(
      'click',
      function () {
        scheduleEnhanceFromUserAction();
      },
      true
    );

    document.addEventListener(
      'change',
      function (e) {
        var t = e.target;
        if (!t) return;
        if (t.name === POSITION_NAME) {
          onPreviewPositionChange();
          return;
        }
        if (isPersonalizationInput(t)) onPersonalizationChange();
      },
      true
    );

    document.addEventListener(
      'input',
      function (e) {
        var t = e.target;
        if (!t || !isPersonalizationInput(t)) return;
        onPersonalizationChange();
      },
      true
    );

    // B4: ESC-close leaves the body modal-open class stuck; listen for Escape.
    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) {
          schedule(syncBodyModalOpenClass, 0);
          schedule(syncBodyModalOpenClass, 120);
        }
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
